import { SetStateAction, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import Layout from "../../app/layout/Layout";
import { Site } from "../../app/models/site";
import agent from "../../app/api/agent";
import LoadingComponent from "../../app/layout/LoadingComponent";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useOrganization } from "@clerk/clerk-react";
import {
  EuiEmptyPrompt,
  EuiLink,
  EuiSelect,
  EuiSpacer,
  EuiSuperDatePicker,
  EuiSuperDatePickerProps,
  EuiTitle,
  OnRefreshProps,
  OnTimeChangeProps,
} from "@elastic/eui";
import { EuiPageHeader, EuiButton } from "@elastic/eui";
import { Add } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useFlow } from "@frigade/react";
import SwaggerViewer from "../analytics/SwaggerViewer";
import APICoverage from "../analytics/APICoverage";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import UploadOpenAPIScheme from "./UploadOpenAPIScheme";

export default function Coverage() {
  const [selectedOption, setSelectedOption] = useState("");
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState<Site[]>([]);
  const { organization } = useOrganization();

  const handleOptionChange = (event: any) => {
    setSelectedOption(event.target.value);
  };

  const [recentlyUsedRanges, setRecentlyUsedRanges] = useState<
    NonNullable<EuiSuperDatePickerProps["recentlyUsedRanges"]>
  >([]);

  const [isLoading, setIsLoading] = useState(false);

  const [start, setStart] = useState("now-12h");
  const [end, setEnd] = useState("now");

  const [refreshKey, setRefreshKey] = useState(Date.now());

  const onTimeChange = ({ start, end }: OnTimeChangeProps) => {
    const recentlyUsedRange = recentlyUsedRanges.filter((recentlyUsedRange) => {
      const isDuplicate =
        recentlyUsedRange.start === start && recentlyUsedRange.end === end;
      return !isDuplicate;
    });
    recentlyUsedRange.unshift({ start, end });
    setStart(start);
    setEnd(end);
    setRecentlyUsedRanges(
      recentlyUsedRange.length > 10
        ? recentlyUsedRange.slice(0, 9)
        : recentlyUsedRange
    );
    setIsLoading(true);
    startLoading();
  };

  const onRefresh = ({ start, end }: OnRefreshProps) => {
    setRefreshKey(Date.now()); // force a re-render
    setIsLoading(true);
    startLoading();
  };

  const startLoading = () => {
    setTimeout(stopLoading, 1000);
  };

  const stopLoading = () => {
    setIsLoading(false);
  };

  const { flow } = useFlow("flow_6fi97SYs");

  useEffect(() => {
    setLoading(true);
    agent.Catalog.list()
      .then((sites) => {
        setSites(sites);
        if (sites.length > 0) {
          flow?.steps.get("step-5")?.complete();
        }
      })
      .catch((error) => console.log(error))
      .finally(() => setLoading(false));
  }, [organization?.id, flow]);

  const siteArray = sites.map((site) => site.hackgatedName);

  const selectedSite = sites.find(
    (site) => site.hackgatedName === selectedOption
  );

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 350,
      },
    },
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 1200,
    maxHeight: "90vh", // limit height to viewport
    overflowY: "auto", // enable vertical scroll
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
  };

  if (loading) return <LoadingComponent message="Loading website data..." />;

  if (sites.length === 0)
    return (
      <Layout>
        <EuiPageHeader
          pageTitle="Comprehensiveness Coverage"
          // iconType="logoKibana"
          description="Uses OpenAPI definitions to validate endpoint coverage, helping identify blind spots and proving the thoroughness of API security testing."
          // rightSideItems={[
          //   <EuiButton fill>Add something</EuiButton>,
          //   <EuiButton>Do something</EuiButton>,
          // ]}
          paddingSize="none"
        />
        <EuiSpacer />

        <Card variant="outlined" sx={{ mb: 2, borderRadius: "12px" }}>
          <CardContent>
            <Box
              sx={{
                height: 400,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EuiEmptyPrompt
                actions={[
                  <EuiButton color="primary" fill>
                    Add a website
                  </EuiButton>,
                ]}
                body={<p>Add a new case or change your filter settings.</p>}
                color="subdued"
                // iconType="logoSecurity"
                layout="vertical"
                title={<h2>Start adding websites</h2>}
                titleSize="m"
              />
            </Box>
          </CardContent>
        </Card>
      </Layout>
    );

  return (
    <Layout>
      <EuiPageHeader
        pageTitle="Comprehensiveness Coverage"
        // iconType="logoKibana"
        description="Uses OpenAPI definitions to validate endpoint coverage, helping identify blind spots and proving the thoroughness of API security testing."
        // rightSideItems={[
        //   <EuiButton fill>Add something</EuiButton>,
        //   <EuiButton>Do something</EuiButton>,
        // ]}
        paddingSize="none"
      />
      <EuiSpacer />

      {/* <EuiSelect
        options={siteArray.map((hackgatedName, index) => ({ value: hackgatedName, text: hackgatedName }))}
        onChange={(e) => {
          // Handle the select change event
          console.log('Selected value:', e.target.value);
        }}
      /> */}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <UploadOpenAPIScheme id={selectedSite?.id} />
        </Box>
      </Modal>

      <Grid container alignItems="center" spacing={3} mb={3}>
        <Grid item xs={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="site-select-label">Select a site</InputLabel>
            <Select
              labelId="site-select-label"
              id="site-select"
              value={selectedOption}
              label="Select a site"
              onChange={handleOptionChange}
              sx={{ borderRadius: "8px" }}
            >
              {sites.map((sites) => (
                <MenuItem key={sites.hackgatedName} value={sites.hackgatedName}>
                  {sites.hackgatedName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={8}>
          <EuiSuperDatePicker
            onTimeChange={onTimeChange}
            start={start}
            onRefresh={onRefresh}
            end={end}
          />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 2, borderRadius: "12px" }}>
        <CardContent>
          {selectedOption === "" ? (
            <Box
              sx={{
                height: 400,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EuiEmptyPrompt
                // iconType="logoSecurity"
                title={<h2>Select a website</h2>}
                body={
                  <p>
                    Select or add a new website or change your filter settings.
                  </p>
                }
                actions={
                  <Button
                    component={Link}
                    to="/catalog/add-site"
                    variant="contained"
                    startIcon={<Add />}
                  >
                    Add a website
                  </Button>
                }
                footer={
                  <>
                    <EuiTitle size="xxs">
                      <h3>Want to learn more?</h3>
                    </EuiTitle>
                    <EuiLink
                      href="https://www.hackgate.io/how-does-hackgate-work"
                      target="_blank"
                    >
                      Read the docs
                    </EuiLink>
                  </>
                }
              />
            </Box>
          ) : (
            ""
          )}

          {selectedOption !== "" ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: "12px" }}>
                  <CardContent>
                    Upload API definition file
                    <Tooltip title="Upload the web app API definition file in .json or .yaml format.">
                      <IconButton>
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      sx={{ borderRadius: "8px" }}
                      onClick={handleOpen}
                    >
                      upload
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: "12px" }}>
                  <CardContent>
                    <APICoverage
                      key={`chart-main-${refreshKey}`}
                      url={selectedOption}
                      gte={start}
                      lte={end}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: "12px" }}>
                  <CardContent>
                    <SwaggerViewer
                      key={`swagger-${selectedSite?.id}-${refreshKey}`}
                      siteId={selectedOption}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            ""
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}
